import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import {
  endAllSessions,
  markLoggedOut,
  purgeLegacyToken,
  refreshAccessToken,
  startLogin,
  takeLoggedOut,
} from '../utils/connect';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const REFRESH_MARGIN_S = 60;

export const AuthProvider = ({ children }) => {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginError, setLoginError] = useState('');

  // The access token lives here and nowhere else: never in localStorage, sessionStorage or a
  // cookie the page can read. A reload gets a new one from the httpOnly refresh cookie.
  const tokenRef = useRef(null);
  const timerRef = useRef(null);

  const scheduleRefresh = useCallback((expiresIn) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const data = await refreshAccessToken();
        if (data) {
          tokenRef.current = data.access_token;
          scheduleRefresh(data.expires_in);
        }
      } catch {
        // Connect busy: the next 401 retries the refresh.
      }
    }, Math.max((expiresIn - REFRESH_MARGIN_S) * 1000, 5000));
  }, []);

  const adoptTokens = useCallback(({ access_token: accessToken, expires_in: expiresIn }) => {
    tokenRef.current = accessToken;
    scheduleRefresh(expiresIn);
  }, [scheduleRefresh]);

  // One request decides everything. Branch strictly on the status: a redirect on anything but
  // a lost session is a login loop for the whole incident.
  const loadMe = useCallback(async () => {
    const response = await fetch(`${API_BASE_URL}/me`, {
      headers: { Authorization: `Bearer ${tokenRef.current}` },
      credentials: 'include',
    });
    if (response.ok) {
      setCurrentUser(await response.json());
      setIsAuthenticated(true);
      setLoginError('');
      return true;
    }
    if (response.status === 403) {
      // Valid Connect account, but no globe-data account or no admin right. No redirect.
      setLoginError("Votre compte Globe n'a pas accès à Globe Data. Contactez un administrateur.");
    } else if (response.status === 401) {
      setLoginError('Session expirée. Cliquez sur « Se connecter » pour reprendre.');
    } else {
      // 5xx, 503: no redirect either, Connect would hand back a token and /me would fail again.
      setLoginError(`Service indisponible (erreur ${response.status}). Réessayez dans un instant.`);
    }
    return false;
  }, []);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    // Callers pass "/tables" or `${API_BASE_URL}/tables`; both must land on the API once.
    if (!url.startsWith('http') && !url.startsWith(API_BASE_URL)) {
      url = `${API_BASE_URL}${url}`;
    }
    const call = () => {
      const headers = new Headers(options.headers);
      if (url.startsWith(API_BASE_URL)) headers.set('Authorization', `Bearer ${tokenRef.current}`);
      return fetch(url, { ...options, credentials: 'include', headers });
    };

    const sentWith = tokenRef.current;
    let response = await call();
    if (response.status === 401) {
      if (tokenRef.current !== sentWith) {
        response = await call(); // someone else refreshed: just retry
      } else {
        // Expired access token: one refresh, one retry. Still 401 -> the session is gone.
        let data = null;
        try {
          data = await refreshAccessToken();
        } catch {
          return response; // Connect busy: an error for this call, not a logout
        }
        if (data) {
          adoptTokens(data);
          response = await call();
        }
      }
      if (response.status === 401) {
        setIsAuthenticated(false);
        setCurrentUser(null);
        startLogin().catch(() => setLoginError('Connexion impossible'));
      }
    }
    // 403 and 5xx come back as they are: no logout, the caller shows the error.
    return response;
  }, [adoptTokens]);

  const login = useCallback(() => {
    startLogin().catch(() => setLoginError('Connexion impossible'));
  }, []);

  const logout = async () => {
    clearTimeout(timerRef.current);
    tokenRef.current = null;
    // This app's refresh chain and the shared SSO session, then Connect's form.
    const ssoEnded = await endAllSessions();
    setIsAuthenticated(false);
    setCurrentUser(null);
    if (ssoEnded) {
      login();
    } else {
      // Connect's session cannot be ended from here: an automatic login would sign back in.
      markLoggedOut();
      setLoginError("Vous êtes déconnecté de Globe Data. Votre session Globe reste ouverte sur ce navigateur : fermez-le pour la terminer, ou cliquez sur « Se connecter » pour reprendre.");
    }
  };

  // /auth/callback hands the tokens over, then /me decides.
  const completeLogin = useCallback(async (tokens) => {
    adoptTokens(tokens);
    return loadMe();
  }, [adoptTokens, loadMe]);

  useEffect(() => {
    purgeLegacyToken();

    // The callback page drives its own flow.
    if (window.location.pathname === '/auth/callback') {
      setIsAuthenticating(false);
      return undefined;
    }

    const boot = async () => {
      try {
        const data = await refreshAccessToken();
        if (data) {
          adoptTokens(data);
          await loadMe();
        } else if (takeLoggedOut()) {
          // A logout that could not end the SSO session: wait for a click, or Connect signs the
          // user straight back in.
          setLoginError("Vous êtes déconnecté de Globe Data. Votre session Globe reste ouverte sur ce navigateur : fermez-le pour la terminer, ou cliquez sur « Se connecter » pour reprendre.");
        } else {
          // No session: straight to Connect, nothing to click. Connect answers with a code at
          // once when its SSO session is open, with its form otherwise.
          await startLogin();
          return; // the page is leaving for Connect
        }
      } catch (error) {
        console.error('Auth boot failed:', error);
        setLoginError('Service indisponible. Réessayez dans un instant.');
      }
      setIsAuthenticating(false);
    };
    boot();

    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{
      isAuthenticating,
      isAuthenticated,
      currentUser,
      loginError,
      fetchWithAuth,
      login,
      logout,
      completeLogin
    }}>
      {children}
    </AuthContext.Provider>
  );
};
